import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import './course-catalogue.scss'
import { EllipsisOutlined } from '@ant-design/icons'
import { isMobile } from '../../../../utils'

// “更多(…)按钮 + 单个 tab 间距”在容器中预留的宽度，用于溢出计算
// （与 course-catalogue.scss 中 .catalogue-more / .catalogue-tab 的尺寸保持一致）
const MORE_BLOCK = 52 // 按钮 44px + 间距 8px
const TAB_GAP = 8 // 单个 tab 的右侧间距

const cn = (...names) => names.filter(Boolean).join(' ')

/**
 * 课程目录（移动端 Tab 形态，宽屏下同样适用）
 * - 顶部一行 tab 展示章/课时，放不下时右侧出现“…”按钮，多余项收进下拉
 * - 两级目录(单元->课时)时，下方展示当前选中单元下的课时列表，点击进入对应课时
 * - 单级目录时 tab 即课时，点击直接切换
 * - 课程资源见移动端组件 course-resources.js
 */
const CourseCatalogue = ({
  markdown,
  defaultActiveKey,
  setDefaultActiveKey,
  courseSelecteHandler
}) => {
  const data = markdown?.data || {}
  const units = data.units || []
  const sectionId = data.sectionid
  const isSeries = !!data.is_series
  // 两级（章下有课时）还是单级（章本身即课时）
  const isNested = units.some(unit => unit.units && unit.units.length)
  const total = units.length
  const showCatalogue = isSeries && total > 0

  // 当前激活目录项下标（两级优先跟随父级的 defaultActiveKey，单级按课时 id 定位）
  const activeIndex = useMemo(() => {
    if (!total) return 0
    let index
    if (isNested) {
      index = Number(defaultActiveKey) || 0
    } else {
      index = units.findIndex(course => course.id === sectionId)
      if (index < 0) index = Number(defaultActiveKey) || 0
    }
    if (index < 0 || index >= total) return 0
    return index
  }, [units, isNested, sectionId, defaultActiveKey, total])

  // ---------- 溢出计算：一行放不下的 tab 收进“…” ----------
  const [visibleCount, setVisibleCount] = useState(null) // null=尚未计算(全量渲染)
  const [moreOpen, setMoreOpen] = useState(false)
  const tabsWrapRef = useRef(null)
  const tabRefs = useRef([])
  const moreBtnRef = useRef(null)
  const popRef = useRef(null)
  const foldIndex = visibleCount == null ? total : visibleCount
  const hasMore = total > foldIndex
  const activeIsFolded = hasMore && activeIndex >= foldIndex

  const measure = useCallback(() => {
    const wrap = tabsWrapRef.current
    if (!wrap || !total) return
    const els = tabRefs.current.filter(Boolean)
    if (!els.length) return
    const containerWidth = wrap.clientWidth
    const widths = els.map(el => el.offsetWidth)
    // 全部项总宽（相邻项之间的间距）
    const totalWidth =
      widths.reduce((sum, width) => sum + width, 0) +
      TAB_GAP * Math.max(0, els.length - 1)
    // 一行放得下：不出现“…”按钮
    if (totalWidth <= containerWidth) {
      setVisibleCount(prev => (prev === total ? prev : total))
      return
    }
    // 放不下：从前往后累加宽度，给“…”按钮预留空间
    let acc = 0
    let count = 0
    for (let i = 0; i < els.length; i++) {
      const need = count > 0 ? TAB_GAP : 0 // 当前项与上一项之间的间距
      if (acc + need + widths[i] > containerWidth - MORE_BLOCK) break
      acc += need + widths[i]
      count++
    }
    count = Math.max(1, count) // 至少展示一个
    setVisibleCount(prev => (prev === count ? prev : count))
  }, [total])

  // DOM 就绪后及目录变化时重新测量
  useLayoutEffect(() => {
    measure()
  }, [measure, total, showCatalogue])

  // 容器尺寸变化(窗口/布局)时重新测量
  useEffect(() => {
    const wrap = tabsWrapRef.current
    if (!wrap || !showCatalogue) return undefined
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure)
      observer.observe(wrap)
      return () => observer.disconnect()
    }
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure, showCatalogue])

  // 点击空白区域关闭“…”下拉
  useEffect(() => {
    if (!moreOpen) return undefined
    const handleDocDown = e => {
      if (
        popRef.current?.contains(e.target) ||
        moreBtnRef.current?.contains(e.target)
      ) {
        return
      }
      setMoreOpen(false)
    }
    document.addEventListener('mousedown', handleDocDown)
    document.addEventListener('touchstart', handleDocDown)
    return () => {
      document.removeEventListener('mousedown', handleDocDown)
      document.removeEventListener('touchstart', handleDocDown)
    }
  }, [moreOpen])

  // 点章 tab：两级->切换下方列表；单级->直接进入课时
  const openTab = index => {
    setMoreOpen(false)
    if (isNested) {
      setDefaultActiveKey && setDefaultActiveKey(index)
    } else {
      courseSelecteHandler && courseSelecteHandler(units[index])
    }
  }

  if (!showCatalogue) return null

  const unitText = count => (isNested ? `${count} 个单元` : `${count} 个课时`)
  const serialText = index =>
    isNested ? `第 ${index + 1} 单元` : `第 ${index + 1} 课`
  // 当前单元下的课时列表（两级）
  const chapter = units[activeIndex] || {}
  const sections = (isNested && chapter.units) || []
  if (isMobile()) {
    return (
      <div className="col col--4 sidebar">
        {showCatalogue && (
          <section className="catalogue-card">
            <header className="catalogue-head">
              <span className="catalogue-head-title">课程目录</span>
              <span className="catalogue-head-count">{unitText(total)}</span>
            </header>
  
            <nav className="catalogue-tabs" ref={tabsWrapRef}>
              {units.map((unit, index) => (
                <button
                  type="button"
                  key={unit.id ?? index}
                  ref={el => {
                    tabRefs.current[index] = el
                  }}
                  className={cn(
                    'catalogue-tab',
                    index === activeIndex && 'is-active',
                    index >= foldIndex && 'is-folded'
                  )}
                  title={unit.title}
                  onClick={() => openTab(index)}
                >
                  {unit.title || serialText(index)}
                </button>
              ))}
              {hasMore && (
                <button
                  type="button"
                  ref={moreBtnRef}
                  className={cn('catalogue-more', moreOpen && 'is-open')}
                  onClick={() => setMoreOpen(open => !open)}
                >
                  {activeIsFolded && <i className="catalogue-more-dot" />}
                  <EllipsisOutlined />
                </button>
              )}
            </nav>
  
            {hasMore && moreOpen && (
              <div className="catalogue-pop" ref={popRef}>
                {units.slice(foldIndex).map((unit, i) => {
                  const index = foldIndex + i
                  return (
                    <div
                      key={unit.id ?? index}
                      className={cn(
                        'catalogue-pop-item',
                        index === activeIndex && 'is-active'
                      )}
                      onClick={() => openTab(index)}
                    >
                      <span className="catalogue-pop-serial">
                        {serialText(index)}
                      </span>
                      <span className="catalogue-pop-title">
                        {unit.title || serialText(index)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
  
            {isNested && (
              <div className="catalogue-list">
                {sections.length ? (
                  sections.map((course, index) => (
                    <div
                      key={course.id ?? index}
                      className={cn(
                        'catalogue-list-item',
                        course.id === sectionId && 'is-active'
                      )}
                      onClick={() => courseSelecteHandler && courseSelecteHandler(course)}
                    >
                      <span className="catalogue-list-index">{index + 1}</span>
                      <span className="catalogue-list-name" title={course.title}>
                        {course.title}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="catalogue-empty">本单元暂无可学习课时</p>
                )}
              </div>
            )}
          </section>
        )}
  
      </div>
    )
  } else {
    return null
  }
}

export default CourseCatalogue
